import React, { useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
    ArrowLeft,
    ExternalLink,
    Shield,
    Calendar,
    Activity,
    FileText,
    Siren,
    Lock,
    Copy,
    Zap,
    Server,
    Globe,
    AlertTriangle,
    CheckCircle,
    Box
} from 'lucide-react';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { toast } from 'sonner';
import { useLanguage } from '@/hooks/useLanguage';

// --- Interfaces ---

interface ShodanCVEData {
    cve_id: string;
    summary: string;
    cvss: number;
    cvss_version: number;
    cvss_v2?: number;
    cvss_v3?: number;
    epss: number;
    ranking_epss: number;
    kev: boolean;
    propose_action?: string;
    ransomware_campaign?: string;
    references: string[];
    published_time: string;
    cpes: string[];
}

// --- Helpers ---

const parseCvssVector = (vector: string) => {
    if (!vector) return {};
    // Example 3.1: CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:N/A:N
    const parts = vector.split('/');
    const map: Record<string, string> = {};

    parts.forEach(part => {
        const [key, value] = part.split(':');
        if (key && value) map[key] = value;
    });

    const translate = (key: string, val: string) => {
        const dict: Record<string, Record<string, string>> = {
            // Common
            AV: { N: 'Sieć (Network)', A: 'Sąsiednia (Adjacent)', L: 'Lokalna (Local)', P: 'Fizyczna (Physical)' },
            AC: { L: 'Niska (Low)', H: 'Wysoka (High)' },
            PR: { N: 'Brak (None)', L: 'Niskie (Low)', H: 'Wysokie (High)' },
            UI: { N: 'Brak (None)', R: 'Wymagana (Required)' },
            S: { U: 'Niezmieniony (Unchanged)', C: 'Zmieniony (Changed)' },
            C: { N: 'Brak (None)', L: 'Niska (Low)', H: 'Wysoka (High)' },
            I: { N: 'Brak (None)', L: 'Niska (Low)', H: 'Wysoka (High)' },
            A: { N: 'Brak (None)', L: 'Niska (Low)', H: 'Wysoka (High)' },
        };
        return dict[key]?.[val] || val;
    };

    return {
        attackVector: translate('AV', map['AV']),
        attackComplexity: translate('AC', map['AC']),
        privilegesRequired: translate('PR', map['PR']),
        userInteraction: translate('UI', map['UI']),
        scope: translate('S', map['S']),
        confidentialityImpact: translate('C', map['C']),
        integrityImpact: translate('I', map['I']),
        availabilityImpact: translate('A', map['A']),
        raw: map
    };
};

const parseCpe = (cpe: string) => {
    // cpe:2.3:a:vendor:product:version:update:edition:language:sw_edition:target_sw:target_hw:other
    const parts = cpe.split(':');
    if (parts.length < 5) return { vendor: cpe, product: '', version: '' };

    return {
        vendor: parts[3].replace(/_/g, ' '),
        product: parts[4].replace(/_/g, ' '),
        version: parts[5] === '*' ? 'Wszystkie wersje' : parts[5].replace(/_/g, ' ')
    };
};

// --- Components ---

const CircularGauge = ({ score, max = 10, label, colorClass }: { score: number, max?: number, label: string, colorClass: string }) => {
    const radius = 36;
    const circumference = 2 * Math.PI * radius;
    const percentage = (score / max) * 100;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
        <div className="flex flex-col items-center justify-center">
            <div className="relative h-28 w-28 flex items-center justify-center">
                <svg className="h-full w-full -rotate-90" viewBox="0 0 96 96">
                    {/* Background Circle */}
                    <circle
                        className="text-muted/20"
                        strokeWidth="8"
                        stroke="currentColor"
                        fill="transparent"
                        r={radius}
                        cx="48"
                        cy="48"
                    />
                    {/* Progress Circle */}
                    <circle
                        className={`${colorClass} transition-all duration-1000 ease-out`}
                        strokeWidth="8"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                        r={radius}
                        cx="48"
                        cy="48"
                    />
                </svg>
                <div className="absolute flex flex-col items-center">
                    <span className={`text-3xl font-bold ${colorClass}`}>{score.toFixed(1)}</span>
                </div>
            </div>
            <span className="mt-2 text-sm font-medium text-muted-foreground uppercase tracking-wider">{label}</span>
        </div>
    );
};

const CVEDetail = () => {
    const { cveId } = useParams<{ cveId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { language } = useLanguage();

    const stateDescEn = location.state?.description_en;
    const stateDescPl = location.state?.description_pl;

    // Fallback to old single description if new format not present (backward compatibility)
    const stateDescLegacy = location.state?.description;

    const displayDescription = language === 'en'
        ? (stateDescEn || stateDescLegacy)
        : (stateDescPl || stateDescLegacy);

    // Fetch Shodan Data
    const { data: cveData, isLoading, error } = useQuery({
        queryKey: ['cve', cveId],
        queryFn: async () => {
            if (!cveId) throw new Error('No CVE ID');
            const res = await fetch(`/api/shodan/cve/${cveId}`);
            if (!res.ok) throw new Error('Failed to fetch CVE data');
            return res.json() as Promise<ShodanCVEData>;
        },
        enabled: !!cveId,
    });

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast.success(`Skopiowano ${label} do schowka`);
    };

    // Helper for colors
    const getSeverityColor = (score: number) => {
        if (score >= 9.0) return "text-red-600 dark:text-red-500";
        if (score >= 7.0) return "text-orange-600 dark:text-orange-500";
        if (score >= 4.0) return "text-yellow-600 dark:text-yellow-500";
        return "text-green-600 dark:text-green-500";
    };

    const getSeverityBg = (score: number) => {
        if (score >= 9.0) return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800";
        if (score >= 7.0) return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800";
        if (score >= 4.0) return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800";
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800";
    };

    // Extract vector string from summary if available
    const vectorString = useMemo(() => {
        if (!cveData?.summary) return "";
        const match = cveData.summary.match(/CVSS:3\.[01]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]/)
            || cveData.summary.match(/CVSS:3\.[01]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]/)
            || cveData.summary.match(/CVSS:3\.[01]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]/)
            || cveData.summary.match(/CVSS:3\.[01]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]\/[A-Z]+:[A-Z]/);
        // Fallback for simpler regex
        if (!match) {
            const simpleMatch = cveData.summary.match(/CVSS:3\.[01]\/[^\s)]+/);
            return simpleMatch ? simpleMatch[0] : "";
        }
        return match ? match[0] : "";
    }, [cveData]);

    const parsedVector = useMemo(() => parseCvssVector(vectorString), [vectorString]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow container mx-auto px-4 py-8 pt-24">
                    <div className="space-y-6">
                        <Skeleton className="h-12 w-1/3" />
                        <Skeleton className="h-6 w-1/4" />
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <Skeleton className="h-32" />
                            <Skeleton className="h-32" />
                            <Skeleton className="h-32" />
                        </div>
                        <Skeleton className="h-64" />
                    </div>
                </main>
            </div>
        );
    }

    if (error || !cveData) {
        return (
            <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow container mx-auto px-4 py-8 pt-24 flex items-center justify-center">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold mb-2">Nie znaleziono CVE</h2>
                        <p className="text-muted-foreground mb-4">Nie udało się pobrać danych dla {cveId}</p>
                        <Button onClick={() => navigate('/CVE')}>Powrót do listy</Button>
                    </div>
                </main>
            </div>
        );
    }

    const finalScore = cveData.cvss || 0;
    const finalSeverity = finalScore >= 9 ? 'KRYTYCZNY' : finalScore >= 7 ? 'WYSOKI' : finalScore >= 4 ? 'ŚREDNI' : 'NISKI';
    const isKev = cveData.kev;
    const isRansomware = cveData.ransomware_campaign && cveData.ransomware_campaign !== 'Unknown';

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-grow container mx-auto px-4 py-8 pt-24 animate-in fade-in duration-500">
                {/* Back Button */}
                <div className="mb-8 mt-4">
                    <Button
                        variant="outline"
                        onClick={() => navigate('/CVE')}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Powrót do listy
                    </Button>
                </div>

                {/* --- Header Section --- */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-3 mb-3">
                                <Badge variant="outline" className={`text-sm px-3 py-1 font-bold ${getSeverityBg(finalScore)}`}>
                                    {finalSeverity}
                                </Badge>
                                {isKev && (
                                    <Badge variant="destructive" className="animate-pulse flex items-center gap-1 shadow-red-500/50 shadow-lg px-3 py-1 border-2 border-red-500 text-base">
                                        <Siren className="h-5 w-5" />
                                        CISA KEV: Aktywna eksploatacja
                                    </Badge>
                                )}
                                {isRansomware && (
                                    <Badge variant="default" className="bg-purple-600 hover:bg-purple-700 flex items-center gap-1 shadow-purple-500/50 shadow-lg px-3 py-1 border-2 border-purple-400 text-base">
                                        <Lock className="h-5 w-5" />
                                        Ransomware: {cveData.ransomware_campaign}
                                    </Badge>
                                )}
                            </div>
                            <h1 className="text-4xl md:text-6xl font-black tracking-tight text-foreground mb-3 flex items-center gap-3">
                                {cveData.cve_id}
                                <Button variant="ghost" size="icon" onClick={() => copyToClipboard(cveData.cve_id, "CVE ID")}>
                                    <Copy className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                                </Button>
                            </h1>

                            <div className="flex items-center gap-6 mt-4 text-sm text-muted-foreground">
                                {cveData.published_time && (
                                    <span className="flex items-center">
                                        <Calendar className="h-4 w-4 mr-2" />
                                        Opublikowano: {format(new Date(cveData.published_time), 'd MMMM yyyy', { locale: pl })}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- Dashboard Grid --- */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">

                    {/* Left Column: Metrics (4 cols) */}
                    <div className="lg:col-span-4 space-y-6">
                        {/* CVSS Card */}
                        <Card className="glass-card border-t-4 border-t-primary shadow-lg hover:shadow-xl transition-all relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-4 opacity-10">
                                <Activity className="h-24 w-24" />
                            </div>
                            <CardContent className="pt-6 flex flex-col items-center text-center">
                                <CircularGauge score={finalScore} label="CVSS Score" colorClass={getSeverityColor(finalScore)} />
                                <div className="mt-4">
                                    <p className="text-sm font-medium text-muted-foreground">Wersja CVSS: {cveData.cvss_version}</p>
                                    {vectorString && (
                                        <code className="text-xs bg-secondary/50 px-2 py-1 rounded mt-2 block break-all">
                                            {vectorString}
                                        </code>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* EPSS Card */}
                        <Card className="glass-card border-t-4 border-t-blue-500 shadow-lg hover:shadow-xl transition-all">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Zap className="h-5 w-5 text-blue-500" />
                                    EPSS (Exploit Prediction)
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-muted-foreground">Prawdopodobieństwo</span>
                                            <span className="font-bold text-blue-600">{(cveData.epss * 100).toFixed(2)}%</span>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-muted-foreground">Percentyl</span>
                                            <span className="font-bold text-indigo-600">{(cveData.ranking_epss * 100).toFixed(2)}%</span>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Vector Breakdown Card */}
                        {vectorString && (
                            <Card className="shadow-md">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <Activity className="h-5 w-5 text-primary" />
                                        Szczegóły Wektora
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 gap-y-4 gap-x-2 text-sm">
                                        <div className="flex flex-col">
                                            <span className="text-xs text-muted-foreground">Wektor ataku (AV)</span>
                                            <span className="font-medium">{parsedVector.attackVector || "N/A"}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-muted-foreground">Złożoność (AC)</span>
                                            <span className="font-medium">{parsedVector.attackComplexity || "N/A"}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-muted-foreground">Uprawnienia (PR)</span>
                                            <span className="font-medium">{parsedVector.privilegesRequired || "N/A"}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-muted-foreground">Interakcja (UI)</span>
                                            <span className="font-medium">{parsedVector.userInteraction || "N/A"}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-muted-foreground">Zakres (S)</span>
                                            <span className="font-medium">{parsedVector.scope || "N/A"}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-muted-foreground">Poufność (C)</span>
                                            <span className="font-medium">{parsedVector.confidentialityImpact || "N/A"}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-muted-foreground">Integralność (I)</span>
                                            <span className="font-medium">{parsedVector.integrityImpact || "N/A"}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs text-muted-foreground">Dostępność (A)</span>
                                            <span className="font-medium">{parsedVector.availabilityImpact || "N/A"}</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Right Column: Details (8 cols) */}
                    <div className="lg:col-span-8 space-y-6">

                        {/* Description & Remediation */}
                        <Card className="shadow-md">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="h-5 w-5 text-primary" />
                                    Opis i Analiza
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <p className="text-lg leading-relaxed text-foreground/90">
                                    {displayDescription || cveData.summary || "Brak opisu."}
                                </p>

                                {cveData.propose_action && (
                                    <div className="mt-4 p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg flex gap-3">
                                        <Shield className="h-6 w-6 text-green-600 dark:text-green-400 flex-shrink-0" />
                                        <div>
                                            <h4 className="font-semibold text-green-800 dark:text-green-300">Rekomendacja Naprawcza</h4>
                                            <p className="text-green-700 dark:text-green-400 mt-1">{cveData.propose_action}</p>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* CPEs - Enhanced Visualization */}
                        {cveData.cpes && cveData.cpes.length > 0 && (
                            <Card className="shadow-md">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Server className="h-5 w-5 text-primary" />
                                        Dotknięte Produkty (CPE)
                                    </CardTitle>
                                    <CardDescription>
                                        Lista produktów i wersji podatnych na to zagrożenie.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {cveData.cpes.map((cpeString, idx) => {
                                            const { vendor, product, version } = parseCpe(cpeString);
                                            return (
                                                <div key={idx} className="flex items-start p-3 rounded-lg border border-border bg-secondary/20 hover:bg-secondary/40 transition-colors">
                                                    <div className="mr-3 mt-1 bg-primary/10 p-2 rounded-full">
                                                        <Box className="h-4 w-4 text-primary" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <span className="font-semibold text-sm capitalize truncate" title={vendor}>
                                                                {vendor}
                                                            </span>
                                                        </div>
                                                        <div className="text-base font-bold text-foreground mb-1 truncate" title={product}>
                                                            {product}
                                                        </div>
                                                        <div className="flex items-center text-xs text-muted-foreground">
                                                            <span className="font-mono bg-background px-1.5 py-0.5 rounded border border-border">
                                                                v{version}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* References */}
                        {cveData.references && cveData.references.length > 0 && (
                            <Card className="shadow-md">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Globe className="h-5 w-5 text-primary" />
                                        Referencje
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="space-y-2">
                                        {cveData.references.map((ref, idx) => (
                                            <li key={idx}>
                                                <a
                                                    href={ref}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-blue-500 hover:underline flex items-center gap-2 break-all"
                                                >
                                                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                                    {ref}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>
                        )}

                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default CVEDetail;
